import { Component, ChangeDetectionStrategy, inject, signal, OnInit } from "@angular/core";
import { ReactiveFormsModule, FormControl, FormGroup, Validators } from "@angular/forms";
import { toSignal } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

import { DynamicDialogRef } from "primeng/dynamicdialog";
import { ButtonModule } from "primeng/button";
import { SelectModule } from "primeng/select";
import { DatePickerModule } from "primeng/datepicker";
import { FloatLabelModule } from "primeng/floatlabel";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";

import { CoreErrorsComponent } from "src/app/shared/components/core-errors/core-errors.component";
import { SalesSupportPerson, SalesSupportPersonClient } from "src/api-service";
import { selectQuoteId, selectSalesInfo } from "src/app/store/pages/quote/quote.selectors";
import { selectUserEmail, selectSelectedCompany } from "src/app/store/user/user.selectors";
import { selectDrawer } from "src/app/store/pages/quote/drawing/drawing.selectors";
import { DrawingActions } from "src/app/store/pages/quote/drawing/drawing.actions";

@Component({
    selector: "app-start-drawing-dialog",
    templateUrl: "./start-drawing-dialog.component.html",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        CoreErrorsComponent,
        ButtonModule,
        SelectModule,
        DatePickerModule,
        FloatLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
    ],
})
export class StartDrawingDialogComponent implements OnInit {

    public readonly ref = inject(DynamicDialogRef);
    private readonly store = inject(Store);
    private readonly client = inject(SalesSupportPersonClient);
    private readonly translate = inject(TranslateService);

    private readonly quoteId = toSignal(this.store.select(selectQuoteId));
    private readonly selectedCompany = toSignal(this.store.select(selectSelectedCompany));
    private readonly userEmail = toSignal(this.store.select(selectUserEmail));
    private readonly currentDrawer = toSignal(this.store.select(selectDrawer));
    private readonly salesInfo = toSignal(this.store.select(selectSalesInfo));

    public readonly drawers = signal<SalesSupportPerson[]>([]);
    public readonly loadingDrawers = signal(true);

    public readonly form = new FormGroup({
        estimatedCompletion: new FormControl<Date | null>(null, [Validators.required]),
        drawerId: new FormControl<number | null>(null, [Validators.required]),
    });

    public ngOnInit(): void {
        const companyId = this.selectedCompany()?.id;
        if (companyId) {
            this.client.getDrawers(companyId).subscribe({
                next: drawers => {
                    this.drawers.set(drawers);
                    this.loadingDrawers.set(false);
                    if (this.form.controls.drawerId.value == null) {
                        const email = this.userEmail()?.toLowerCase();
                        if (email) {
                            const self = drawers.find(d => d.email?.toLowerCase() === email);
                            if (self) {
                                this.form.patchValue({ drawerId: self.id });
                            }
                        }
                    }
                },
                error: () => this.loadingDrawers.set(false),
            });
        }
        // Default to the drawer already assigned at send-for-drawing time
        const currentDrawerId = this.currentDrawer()?.id;
        if (currentDrawerId != null) {
            this.form.patchValue({ drawerId: currentDrawerId });
        }
    }

    public submit(): void {
        const quoteId = this.quoteId();
        const companyId = this.selectedCompany()?.id;
        const { estimatedCompletion, drawerId } = this.form.value;
        if (!quoteId || !companyId || !estimatedCompletion) {
            this.ref.close();
            return;
        }

        const drawerName = this.drawers().find(d => d.id === drawerId)?.name ?? "—";
        const params = {
            quoteId,
            companyId,
            salesPerson: this.salesInfo()?.responsibleSalesPerson?.name ?? "—",
            drawer: drawerName,
            estimatedCompletion: this.formatDate(estimatedCompletion),
            drawingLink: `${window.location.origin}/${companyId}/quote/${quoteId}/drawing`,
        };
        const subject = this.translate.instant("QUOTE.DRAWING.EMAIL.START.SUBJECT", params);
        const message = this.translate.instant("QUOTE.DRAWING.EMAIL.START.BODY", params);

        this.store.dispatch(DrawingActions.startDrawing({
            quoteId,
            estimatedCompletion,
            drawerId: drawerId ?? undefined,
            subject,
            message,
        }));
        this.ref.close();
    }

    /** ISO yyyy-MM-dd — readable in the email regardless of recipient locale. */
    private formatDate(d: Date): string {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
    }

    public closeDialog(): void {
        this.ref.close();
    }
}
